// User feedback and preference learning system
import { collection, addDoc, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

// Submit user feedback for a specific day
export const submitDayFeedback = async (userId, beachDate, actualConditions, rating, issues = []) => {
  try {
    await addDoc(collection(db, 'user_feedback'), {
      userId,
      beachDate,
      actualConditions,
      rating, // 'perfect', 'good', 'meh', 'bad'
      issues, // ['too-hot', 'too-windy', 'too-cloudy', etc.]
      timestamp: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

// Get feedback history for a user
export const getUserFeedback = async (userId, lastNDays = 30) => {
  try {
    const q = query(
      collection(db, 'user_feedback'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(lastNDays)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting feedback:', error);
    return [];
  }
};

// Analyze feedback and suggest preference adjustments
export const suggestPreferenceAdjustments = (feedbackHistory, currentPreferences) => {
  if (!feedbackHistory || feedbackHistory.length < 3) {
    return []; // Need at least 3 feedback entries to suggest changes
  }

  const suggestions = [];

  // Analyze temperature preferences
  const tempFeedback = feedbackHistory.filter(f =>
    f.rating === 'perfect' && f.actualConditions.temp
  );

  if (tempFeedback.length >= 3) {
    const temps = tempFeedback.map(f => f.actualConditions.temp);
    const avgPerfectTemp = temps.reduce((a, b) => a + b, 0) / temps.length;
    const maxPerfectTemp = Math.max(...temps);
    const minPerfectTemp = Math.min(...temps);

    // Suggest expanding max temp if user enjoyed hotter days
    if (maxPerfectTemp > currentPreferences.tempMax + 2) {
      suggestions.push({
        type: 'tempMax',
        current: currentPreferences.tempMax,
        suggested: Math.round(maxPerfectTemp),
        reason: `You rated ${tempFeedback.filter(f => f.actualConditions.temp > currentPreferences.tempMax).length} days as perfect when temps were above ${currentPreferences.tempMax}°F`,
        confidence: tempFeedback.filter(f => f.actualConditions.temp > currentPreferences.tempMax).length >= 3 ? 'high' : 'medium'
      });
    }

    // Suggest expanding min temp if user enjoyed cooler days
    if (minPerfectTemp < currentPreferences.tempMin - 2) {
      suggestions.push({
        type: 'tempMin',
        current: currentPreferences.tempMin,
        suggested: Math.round(minPerfectTemp),
        reason: `You rated ${tempFeedback.filter(f => f.actualConditions.temp < currentPreferences.tempMin).length} days as perfect when temps were below ${currentPreferences.tempMin}°F`,
        confidence: tempFeedback.filter(f => f.actualConditions.temp < currentPreferences.tempMin).length >= 3 ? 'high' : 'medium'
      });
    }
  }

  // Analyze "too hot" feedback
  const tooHotFeedback = feedbackHistory.filter(f =>
    f.issues?.includes('too-hot') && f.actualConditions.temp
  );
  if (tooHotFeedback.length >= 2) {
    const temps = tooHotFeedback.map(f => f.actualConditions.temp);
    const avgTooHot = temps.reduce((a, b) => a + b, 0) / temps.length;

    if (avgTooHot < currentPreferences.tempMax) {
      suggestions.push({
        type: 'tempMax',
        current: currentPreferences.tempMax,
        suggested: Math.max(70, Math.round(avgTooHot - 3)),
        reason: `You found ${tooHotFeedback.length} days too hot around ${Math.round(avgTooHot)}°F`,
        confidence: 'high'
      });
    }
  }

  // Analyze "too cold" feedback
  const tooColdFeedback = feedbackHistory.filter(f =>
    f.issues?.includes('too-cold') && f.actualConditions.temp
  );
  if (tooColdFeedback.length >= 2) {
    const temps = tooColdFeedback.map(f => f.actualConditions.temp);
    const avgTooCold = temps.reduce((a, b) => a + b, 0) / temps.length;

    if (avgTooCold > currentPreferences.tempMin) {
      suggestions.push({
        type: 'tempMin',
        current: currentPreferences.tempMin,
        suggested: Math.min(90, Math.round(avgTooCold + 3)),
        reason: `You found ${tooColdFeedback.length} days too cold around ${Math.round(avgTooCold)}°F`,
        confidence: 'high'
      });
    }
  }

  // Analyze wind preferences
  const windyFeedback = feedbackHistory.filter(f =>
    f.issues?.includes('too-windy') && f.actualConditions.wind
  );
  if (windyFeedback.length >= 2) {
    const winds = windyFeedback.map(f => f.actualConditions.wind);
    const avgTooWindy = winds.reduce((a, b) => a + b, 0) / winds.length;

    if (avgTooWindy < currentPreferences.maxWindSpeed) {
      suggestions.push({
        type: 'maxWindSpeed',
        current: currentPreferences.maxWindSpeed,
        suggested: Math.max(5, Math.round(avgTooWindy - 2)),
        reason: `You found ${windyFeedback.length} days too windy around ${Math.round(avgTooWindy)}mph`,
        confidence: 'high'
      });
    }
  }

  // Analyze cloud/rain tolerance
  const rainyFeedback = feedbackHistory.filter(f =>
    (f.rating === 'perfect' || f.rating === 'good') &&
    f.actualConditions.precipProb > currentPreferences.maxPrecipChance
  );
  if (rainyFeedback.length >= 2) {
    const maxPrecip = Math.max(...rainyFeedback.map(f => f.actualConditions.precipProb));
    suggestions.push({
      type: 'maxPrecipChance',
      current: currentPreferences.maxPrecipChance,
      suggested: Math.min(50, maxPrecip),
      reason: `You enjoyed ${rainyFeedback.length} days with higher rain chances than your current tolerance`,
      confidence: 'medium'
    });
  }

  return suggestions;
};

// Apply a suggested adjustment
export const applyPreferenceAdjustment = (currentPreferences, suggestion) => {
  return {
    ...currentPreferences,
    [suggestion.type]: suggestion.suggested
  };
};
